import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { likeComment, unlikeComment } from "@/services/LikeService";
import { COMMENT_LIKE_TARGET_TYPE } from "@/constants/like.constants";
import { queuePendingLike } from "@/helpers/pending-like-storage";
import type { MixpanelLikeUnlikeLocation } from "@/constants/mixpanel.constants";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";

type LikeAction = "like" | "unlike";

interface UseCommentLikeOptions {
    commentId: string;
    playablePropertyId?: string;
    initialLikeCount?: number;
    initialLikedByCurrentUser?: boolean;
    userId: string | null;
    isAuthenticated: boolean;
    onRequireAuth: () => void;
    nicknameLiked: string | null;
    mixpanelLocation?: MixpanelLikeUnlikeLocation;
}

interface ToggleLikeOptions {
    onError?: (error?: unknown) => void;
}

function patchCommentLikeInTree(
    nodes: unknown[],
    commentId: string,
    nextLiked: boolean,
    nextCount: number,
): unknown[] {
    return nodes.map((node) => {
        if (!node || typeof node !== "object") {
            return node;
        }
        const record = node as Record<string, unknown>;
        const nodeId = record.id ? String(record.id) : null;
        let nextNode = node;
        if (nodeId === commentId) {
            nextNode = {
                ...record,
                likeCount: nextCount,
                likedByCurrentUser: nextLiked,
            };
        }
        if (Array.isArray(record.replies)) {
            nextNode = {
                ...(nextNode as Record<string, unknown>),
                replies: patchCommentLikeInTree(record.replies as unknown[], commentId, nextLiked, nextCount),
            };
        }
        return nextNode;
    });
}

/**
 * Optimistic comment like toggle with scoreboard comment cache sync.
 */
export default function useCommentLike({
    commentId,
    playablePropertyId,
    initialLikeCount = 0,
    initialLikedByCurrentUser = false,
    userId,
    isAuthenticated,
    onRequireAuth,
    nicknameLiked,
    mixpanelLocation = "Scoreboard",
}: UseCommentLikeOptions) {
    const queryClient = useQueryClient();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { userState } = useUser();
    const requestingUserKey = userId ? String(userId) : "anonymous";
    const [isLiked, setIsLiked] = React.useState(Boolean(initialLikedByCurrentUser));
    const [likeCount, setLikeCount] = React.useState(
        Number.isFinite(initialLikeCount) ? Math.max(0, Number(initialLikeCount)) : 0,
    );
    const lastHydratedSignatureRef = React.useRef<string | undefined>(undefined);

    const applyLikeStateToCaches = React.useCallback(
        (nextLiked: boolean, nextCount: number) => {
            if (playablePropertyId) {
                queryClient.setQueriesData(
                    {
                        queryKey: ["playable-property-comments"],
                        predicate: (query) =>
                            Array.isArray(query.queryKey) &&
                            query.queryKey[0] === "playable-property-comments" &&
                            String(query.queryKey[1] ?? "") === playablePropertyId,
                    },
                    (prev: unknown) => {
                        if (!prev || typeof prev !== "object") {
                            return prev;
                        }
                        const data = (prev as { data?: unknown[] }).data;
                        if (!Array.isArray(data)) {
                            return prev;
                        }
                        return {
                            ...(prev as Record<string, unknown>),
                            data: patchCommentLikeInTree(data, commentId, nextLiked, nextCount),
                        };
                    },
                );
            }
            queryClient.setQueriesData(
                {
                    queryKey: ["comment-like-counts"],
                    predicate: (query) =>
                        Array.isArray(query.queryKey) && query.queryKey[0] === "comment-like-counts",
                },
                (prev: unknown) => {
                    if (!prev || typeof prev !== "object") {
                        return {
                            counts: [
                                {
                                    targetType: COMMENT_LIKE_TARGET_TYPE,
                                    targetId: commentId,
                                    count: nextCount,
                                    likedByCurrentUser: nextLiked,
                                },
                            ],
                        };
                    }
                    const counts = Array.isArray((prev as { counts?: unknown[] }).counts)
                        ? (prev as { counts: Array<Record<string, unknown>> }).counts
                        : [];
                    if (counts.length === 0) {
                        return {
                            counts: [
                                {
                                    targetType: COMMENT_LIKE_TARGET_TYPE,
                                    targetId: commentId,
                                    count: nextCount,
                                    likedByCurrentUser: nextLiked,
                                },
                            ],
                        };
                    }
                    const hasMatch = counts.some((entry) => String(entry.targetId) === commentId);
                    if (!hasMatch) {
                        return {
                            counts: [
                                ...counts,
                                {
                                    targetType: COMMENT_LIKE_TARGET_TYPE,
                                    targetId: commentId,
                                    count: nextCount,
                                    likedByCurrentUser: nextLiked,
                                },
                            ],
                        };
                    }
                    return {
                        counts: counts.map((entry) =>
                            String(entry.targetId) === commentId
                                ? { ...entry, count: nextCount, likedByCurrentUser: nextLiked }
                                : entry,
                        ),
                    };
                },
            );
        },
        [commentId, playablePropertyId, queryClient],
    );

    const mutation = useMutation({
        mutationFn: async (action: LikeAction) => {
            if (!userId) {
                throw new Error("Missing user id");
            }
            if (action === "like") {
                return likeComment(commentId, userId);
            }
            return unlikeComment(commentId, userId);
        },
        onSuccess: (_result, action) => {
            sendMixpanelEvent("Like-Unlike", {
                pageUrl: window.location.href,
                email_liker: userState?.emailAddress ?? null,
                nickname_liker: userState?.nickname ?? null,
                nickname_liked: nicknameLiked,
                action_type: action === "like" ? "Like" : "Unlike",
                content_type: "comment",
                location: mixpanelLocation,
            });
        },
    });

    // Keep local state in sync with the latest server/cache-derived props.
    // We only skip syncing while the mutation is pending so we don't clobber optimistic UI.
    React.useEffect(() => {
        if (mutation.isPending) {
            return;
        }
        const normalizedCount = Number.isFinite(initialLikeCount) ? Math.max(0, Number(initialLikeCount)) : 0;
        const normalizedLiked = Boolean(initialLikedByCurrentUser);
        const signature = `${commentId}:${requestingUserKey}:${normalizedCount}:${normalizedLiked}`;
        if (lastHydratedSignatureRef.current === signature) {
            return;
        }
        lastHydratedSignatureRef.current = signature;
        setIsLiked(normalizedLiked);
        setLikeCount(normalizedCount);
    }, [commentId, initialLikeCount, initialLikedByCurrentUser, mutation.isPending, requestingUserKey]);

    const toggleLike = ({ onError }: ToggleLikeOptions = {}) => {
        if (!isAuthenticated || !userId) {
            if (!isLiked) {
                queuePendingLike({
                    targetType: COMMENT_LIKE_TARGET_TYPE,
                    targetId: commentId,
                });
            }
            onRequireAuth();
            return;
        }
        const nextLiked = !isLiked;
        const action: LikeAction = nextLiked ? "like" : "unlike";
        const previousLiked = isLiked;
        const previousCount = likeCount;
        const nextCount = Math.max(0, likeCount + (nextLiked ? 1 : -1));
        setIsLiked(nextLiked);
        setLikeCount(nextCount);
        applyLikeStateToCaches(nextLiked, nextCount);
        mutation.mutate(action, {
            onError: (error) => {
                setIsLiked(previousLiked);
                setLikeCount(previousCount);
                applyLikeStateToCaches(previousLiked, previousCount);
                if (onError) {
                    onError(error);
                    return;
                }
                toast.error("Could not update like right now. Please try again.", {
                    id: "COMMENT_LIKE_UNAVAILABLE",
                });
            },
        });
    };

    return {
        isLiked,
        likeCount,
        isPending: mutation.isPending,
        toggleLike,
    };
}
